import { createLoginIntent, finishLoginIntent, hasLoginIntent } from './loginIntent'
import { restoreWalletLogin, signInWithWallet } from './siwx'
import { getWalletRuntime } from './walletRuntime'

const SUPPORTED_CHAINS = [1, 56]
const isMobileWalletBrowser = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
let mobileWalletSignInPromise = null
let automaticWalletLoginSuppressed = false
const normalizeAddress = (value) => {
  const address = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return /^0x[a-f0-9]{40}$/.test(address) ? address : ''
}

const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1]
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=')))
  } catch (error) {
    return null
  }
}

export const isLoggedIn = () => {
  const token = localStorage.getItem('token') || ''
  const payload = decodeJwt(token)
  return Boolean(payload && Number(payload.exp) * 1000 > Date.now())
}

export const hasWalletSession = (address) => {
  return isLoggedIn() && normalizeAddress(localStorage.getItem('userAddress')) === normalizeAddress(address)
}

export const clearWalletLogin = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('userAddress')
}

// A deliberate sign-out should not immediately reopen the wallet picker when
// the router returns to the public home page. This module state resets on a
// full page reload, while explicit user-initiated login remains available.
export const suppressAutomaticWalletLogin = () => {
  automaticWalletLoginSuppressed = true
}

export const isAutomaticWalletLoginSuppressed = () => automaticWalletLoginSuppressed

const currentAccount = async (runtime = null) => {
  const walletRuntime = runtime || await getWalletRuntime()
  if (!walletRuntime) return { address: '', chainId: 0 }

  const account = walletRuntime.getAccount(walletRuntime.wagmiAdapter.wagmiConfig)
  const address = normalizeAddress(account && account.address)
  const chainId = Number(account && account.chainId)
  return { address, chainId }
}

export const restoreCurrentWalletLogin = async () => {
  const account = await currentAccount()
  if (!account.address || !SUPPORTED_CHAINS.includes(account.chainId)) return false
  return restoreWalletLogin(account.address, account.chainId)
}

// AppKit restores a persisted wallet connection asynchronously after a reload.
// Wait for that restore before opening a new login flow, so a valid system
// session for the same wallet can be reused without another signature.
export const waitForWalletLoginRestore = async (timeout = 2500) => {
  if (isLoggedIn() || await restoreCurrentWalletLogin()) return true

  const runtime = await getWalletRuntime()
  if (!runtime) return false

  return new Promise((resolve) => {
    let finished = false
    let timer = null
    let unwatch = () => {}
    const done = (restored) => {
      if (finished) return
      finished = true
      if (timer) window.clearTimeout(timer)
      unwatch()
      resolve(restored)
    }

    unwatch = runtime.watchAccount(runtime.wagmiAdapter.wagmiConfig, {
      onChange: async () => {
        if (await restoreCurrentWalletLogin()) done(true)
      },
    })
    timer = window.setTimeout(async () => done(isLoggedIn() || await restoreCurrentWalletLogin()), timeout)
  })
}

export const prepareWalletLogin = async (runtime = null) => {
  const account = await currentAccount(runtime)
  if (account.address && hasWalletSession(account.address)) {
    return Promise.resolve({ loggedIn: true, updated: false })
  }

  const promise = createLoginIntent()
  if (!account.address) return promise
  if (!Number.isInteger(account.chainId) || account.chainId <= 0) return promise
  if (!SUPPORTED_CHAINS.includes(account.chainId)) {
    finishLoginIntent({ loggedIn: false, errorKey: 'unsupportedNetwork' })
  }
  return promise
}

export const startWalletLogin = async () => {
  const promise = createLoginIntent()
  const runtime = await getWalletRuntime()
  if (!runtime) {
    finishLoginIntent({ loggedIn: false, errorKey: 'walletUnavailable' })
    return promise
  }

  const account = await currentAccount(runtime)
  if (account.address && await restoreCurrentWalletLogin()) {
    finishLoginIntent({ loggedIn: true, updated: false })
    return promise
  }
  try {
    runtime.appKit.open()
  } catch (error) {
    finishLoginIntent({ loggedIn: false, errorKey: 'walletUnavailable' })
  }
  return promise
}

export const handleWalletAccountChange = async () => {
  const runtime = await getWalletRuntime()
  const account = await currentAccount(runtime)
  if (!account.address) return
  if (!Number.isInteger(account.chainId) || account.chainId <= 0) return
  if (!SUPPORTED_CHAINS.includes(account.chainId)) {
    if (hasLoginIntent()) finishLoginIntent({ loggedIn: false, errorKey: 'unsupportedNetwork' })
    return
  }
  if (restoreWalletLogin(account.address, account.chainId)) return
  if (!isMobileWalletBrowser || !hasLoginIntent() || mobileWalletSignInPromise) return

  mobileWalletSignInPromise = signInWithWallet(runtime.wagmiAdapter.wagmiConfig, account.address, account.chainId)
    .then(() => finishLoginIntent({ loggedIn: true, updated: true }))
    .catch(() => finishLoginIntent({ loggedIn: false, errorKey: 'signatureCancelled' }))
    .finally(() => {
      mobileWalletSignInPromise = null
    })
}

export const handleWalletDisconnected = () => {
  // AppKit reports a transient disconnected account while it restores a
  // persisted connector after reload. Explicit disconnect is handled by the
  // DISCONNECT_SUCCESS event subscriber, so do not erase the active token here.
  if (hasLoginIntent()) finishLoginIntent({ loggedIn: false, errorKey: 'connectionCancelled' })
}

export const handleWalletModalClosed = async () => {
  // AppKit closes the wallet-picker before it opens the wallet's SIWX
  // signature sheet. A connected account at this point means the login flow
  // is still in progress, not that the user cancelled it.
  if ((await currentAccount()).address) return
  if (!isLoggedIn() && hasLoginIntent()) finishLoginIntent({ loggedIn: false, errorKey: 'signatureCancelled' })
}
